import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';
import type { ComponentLike } from '@glint/template';
import type { SafeString } from '@ember/template';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';
import IconBadge from 'tio-ui/components/icon-badge';
import type { IconBadgeColor } from 'tio-ui/components/icon-badge';
import Collapsible from 'tio-ui/components/collapsible';

/** Semantic intent for the due/use-by chip — mirrors the icon badge's urgency treatment. */
export type TaskCardChipIntent = 'info' | 'success' | 'warning' | 'danger';

const CHIP_INTENT_CLASSES: Record<TaskCardChipIntent, string> = {
  info: 'tio-fg-info tio-bg-info-soft',
  success: 'tio-fg-success tio-bg-success-soft',
  warning: 'tio-fg-warning tio-bg-warning-soft',
  danger: 'tio-fg-danger tio-bg-danger-soft',
};

interface TaskCardSignature {
  Args: {
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    iconColor?: IconBadgeColor;
    title: string | SafeString;
    /** Pre-formatted chip text, e.g. "use by 07/01" or "due 07/15". */
    chip?: string;
    /** Chip color; defaults to 'info' (the pre-existing teal treatment). */
    chipIntent?: TaskCardChipIntent;
    /** One-line subtitle under the title/chip row, always visible (collapsed
     * or expanded) — Figma's Default/Warning/Error copy for submit-evidence.
     * Optional: other task kinds don't set this today. */
    description?: string | SafeString;
    defaultOpen?: boolean;
    /** Render the always-visible `footer` block. Required because a named block
     * is always "present" at the call site — see the template. */
    hasFooter?: boolean;
  };
  Blocks: {
    body: [];
    /** Optional header-row controls (e.g. "View Activity"), rendered before the
     * collapse chevron. A sibling of the title toggle button, not nested inside
     * it, so its own clicks don't also trigger expand/collapse. */
    headerActions?: [];
    /** Always-visible region below the collapsible body, still inside the card.
     * For the commitment a card asks for rather than the detail it holds — the
     * evidence card's terms + Submit, which must be reachable without expanding
     * (the legacy show page renders them flat). */
    footer?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Generic expandable task card for the Needs Your Attention panel: leading
 * icon badge, title, optional due/use-by chip, an optional always-visible
 * description line, and a collapsible body block. Self-manages its open
 * state (seeded by `@defaultOpen`).
 */
export default class TaskCard extends Component<TaskCardSignature> {
  @tracked isOpen = this.args.defaultOpen ?? false;

  /** Stable id linking the toggle button to its collapsible region for a11y. */
  contentId = `task-card-content-${guidFor(this)}`;

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  get chipClass(): string {
    return CHIP_INTENT_CLASSES[this.args.chipIntent ?? 'info'];
  }

  <template>
    <div class="tio-bg-surface rounded-xl border tio-border-default" ...attributes>
      {{! flex-wrap + a real minimum on the title: the actions block is
        shrink-0 (~170px with a labeled header action), so on a nowrap row any
        container narrower than it clamped the flex-1/min-w-0 title button to
        0px — and the title text, overflow-visible, painted straight through
        the action button (#10307, at 375px). Wrapping drops the actions under
        the title instead once both minimums no longer fit side by side. }}
      <div class="tio-row-between w-full flex-wrap items-start gap-3 p-4">
        <button
          type="button"
          class="tio-row min-w-40 flex-1 items-start gap-3 text-left"
          aria-expanded={{if this.isOpen "true" "false"}}
          aria-controls={{this.contentId}}
          {{on "click" this.toggle}}
        >
          <IconBadge @icon={{@icon}} @color={{if @iconColor @iconColor "purple"}} @size="md" />
          <div class="tio-col min-w-0 gap-1">
            <div class="tio-row min-w-0 flex-wrap gap-2">
              <span class="tio-text-sm-semibold tio-fg-primary">{{@title}}</span>
              {{#if @chip}}
                <span class="tio-text-xs-medium {{this.chipClass}} rounded-full px-2 py-0.5">
                  {{@chip}}
                </span>
              {{/if}}
            </div>
            {{#if @description}}
              <span class="tio-text-sm-normal tio-fg-secondary" data-test-task-card-description>
                {{@description}}
              </span>
            {{/if}}
          </div>
        </button>
        <div class="tio-row shrink-0 items-center gap-3">
          {{yield to="headerActions"}}
          <button
            type="button"
            aria-expanded={{if this.isOpen "true" "false"}}
            aria-controls={{this.contentId}}
            aria-label={{if
              this.isOpen
              (t "tuition_assistance.applications.manage.collapse_card")
              (t "tuition_assistance.applications.manage.expand_card")
            }}
            {{on "click" this.toggle}}
          >
            {{#if this.isOpen}}
              <PhCaretUp @size={{16}} class="tio-fg-secondary shrink-0" />
            {{else}}
              <PhCaretDown @size={{16}} class="tio-fg-secondary shrink-0" />
            {{/if}}
          </button>
        </div>
      </div>
      <Collapsible @isOpen={{this.isOpen}} id={{this.contentId}}>
        <div class="px-4 pb-4">
          {{yield to="body"}}
        </div>
      </Collapsible>
      {{! Gated on an arg rather than `has-block`: a named block must be declared
        unconditionally at the call site, so `has-block` is true even for cards
        that yield nothing into it — every card would grow an empty bordered
        strip. }}
      {{#if @hasFooter}}
        <div class="border-t tio-border-default px-4 py-4" data-test-task-card-footer>
          {{yield to="footer"}}
        </div>
      {{/if}}
    </div>
  </template>
}
